import { differenceInMinutes, isSameDay } from 'date-fns'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useParams } from 'react-router-dom'
import { executeGetCouponsIssues, executePostCouponsUsage } from '../../dataAccess/webApi/dao/couponsDao'
import { GetUseReasonDto } from '../../dataAccess/webApi/dto/useReasonDto'
import { translate } from '../../i18n'
import { fromApiHms, getNowTrimedTime, toApiYmd } from '../../utils/dateUtil'
import { nullPropsToUndefined } from '../../utils/objectUtil'
import { castNonNullable } from '../../utils/typeUtil'
import { facilityReservationDeleteUrl, facilityReservationSelectionUpdateUrl, facilityReservationTodayConditionInputUrl } from '../common/constant/appUrl'
import { reservationStatus, yesNo } from '../common/constant/classification'
import { couponMinuteStepUnit } from '../common/constant/couponSetting'
import { OperationId } from '../common/constant/operationLog'
import { canUseCouponFunctionFromSysCtrl } from '../common/coupon'
import { useErrorHandle } from '../common/error/errorHandler'
import { getFacility } from '../common/facility'
import { useOperationLog } from '../common/operationLog'
import { getReservation, isUseReasonCategoryHidden } from '../common/reservation'
import { getUseReason } from '../common/useReason'
import { showLoading } from '../common/store/slices/application'
import { selectSystemControl } from '../common/store/slices/systemControl'
import { ReservationReference } from '../../views/components/common/reservationDetail/reservationDetailGItems'
import { 
  setStringifyNamesChildInfoList, 
  getReservationDecodeMstValue, 
  ReservationSettingDecodeMst 
} from '../common/reservation'
import { getProjectMstDataToRefer } from '../common/projectMst'
import { MEDICAL_DOC_USAGE } from '../common/constant/projectMst'
import { clearInput, clearReservationInfo, clearTodayConditionInputFieldsInfo } from '../common/store/slices/facilityReserveTodayConditionInput'

interface UrlParams {
  reservationNo: string
}

type ReservationDetail = ReservationReference & {
  cancelLimit?: number,
  cancelLimitTime?: number,
  cancelNote?: string,
  cancelExcludeHolidayFlag?: string,
  cancelLimitHolidayOption?: string,
  lunchAcceptFlag: string,
  snackAcceptFlag: string,
  postponeCancelWaitAcceptFlag: string,
  immediatelyReservationFlag: string,
  couponUsageMinute?: number,
  couponUsageInputLimitMinute?: number,
  isTodayConditionInput: string,
  todayConditionInputName?: string | null,
  todayConditionInputEndTime?: string | null,
  isTodayConditionInputFacility: string
  todayConditionInputFacilityName: string | null
  todayConditionInputFacilityPublicFlag: string
  todayConditionInputFacilityFields: {
    bodyTemperature: string
    meal: string
    sleep: string
    defecation: string
  }
  todayConditionFacility: {
    bodyTemperatureMeasurementTime1: string | null
    bodyTemperature1: number | null
    bodyTemperatureMeasurementTime2: string | null
    bodyTemperature2: number | null
    bodyTemperatureMeasurementTime3: string | null
    bodyTemperature3: number | null
    meal: string | null
    eatingMethod: string | null
    milkTime1: string | null
    milkAmount1: string | null
    milkTime2: string | null
    milkAmount2: string | null
    milkTime3: string | null
    milkAmount3: string | null
    mealTime1: string | null
    mealWhat1: string | null
    mealTime2: string | null
    mealWhat2: string | null
    sleep: string | null
    sleepFromTime1: string | null
    sleepToTime1: string | null
    sleepFromTime2: string | null
    sleepToTime2: string | null
    sleepFromTime3: string | null
    sleepToTime3: string | null
    defecation: string | null
    defecationHours1: string | null
    defecationCondition1: string | null
    defecationHours2: string | null
    defecationCondition2: string | null
    defecationHours3: string | null
    defecationCondition3: string | null
    todayConditionNote: string | null
    note: string | null
    status: string | null
    sendMailDatetime: string | null
    updateDatetime: string | null
  }
  canUpdate: boolean,
  canCancel: boolean,
  useReason: GetUseReasonDto,
  reservationNo: string,
  facilityId: string,
}

export const useAction = () => {
  const errorHandle = useErrorHandle()
  const dispatch = useDispatch()
  const history = useHistory()
  const { reservationNo } = useParams<UrlParams>()
  const { addOperationLog, attachAccessData } = useOperationLog()

  const sysCtrl = useSelector(selectSystemControl)

  const [reservationDetail, setReservationDetail] = useState<ReservationDetail>()

  const [couponTotalBalanceMinute, setCouponTotalBalanceMinute] = useState<number>()
  const [isOpenCouponUsageDialog, setIsOpenCouponUsageDialog] = useState(false)

  const reservationUsageMinute = useMemo(() => {
    if (reservationDetail) {
      const minute = differenceInMinutes(reservationDetail.useToDatetime, reservationDetail.useFromDatetime)
      if (minute % couponMinuteStepUnit) {
        // 入力単位以外の時は繰り上げ
        const unitCount = Math.floor(minute / couponMinuteStepUnit)
        return (unitCount + 1) * couponMinuteStepUnit
      } else {
        return minute
      }
    }
  }, [reservationDetail])

  const nowYmd = getNowTrimedTime()
  const isCanUseCouponSysCtrl = canUseCouponFunctionFromSysCtrl(sysCtrl, nowYmd)

  /** クーポン利用が可能か */
  const isCanUseCoupon =
    // クーポン機能有効
    isCanUseCouponSysCtrl &&
    reservationDetail != null &&
    // クーポン利用可能な事業（事業マスタのクーポン利用入力上限時間（分）が0より大きい）
    reservationDetail.couponUsageInputLimitMinute != null &&
    reservationDetail.couponUsageInputLimitMinute > 0 &&
    // クーポン未利用
    reservationDetail.couponUsageMinute == null &&
    // 確定済み
    reservationDetail.status === reservationStatus.fixed &&
    // クーポン残高有
    !!couponTotalBalanceMinute &&
    // 利用日当日
    isSameDay(reservationDetail.usageDate, nowYmd)

  /** クーポン利用済 */
  const isUsedCoupon = reservationDetail?.couponUsageMinute != null

  const initialize = useCallback(
    async (isInitDisplay?: boolean) => {
      // デコードマスタ取得
      const decodeMstValue = await getReservationDecodeMstValue()
      
      const result = await getReservationDetailWithRequirementSetting(reservationNo, decodeMstValue)
      
      const deletingReservationData = {
        ...result,
        childInfoList: setStringifyNamesChildInfoList(result.childInfoList, decodeMstValue),
      }
      
      if (isInitDisplay) {
        attachAccessData({
          accessData: [
            ... deletingReservationData.childInfoList.map((child) => ({
              userIdRegFlag: yesNo.yes, 
              childId: child.childId,
              usageDate: toApiYmd(result.usageDate), 
              reservationNo
            }))
          ],
        })
      }

      if (isCanUseCouponSysCtrl) {
        const couponTotalBalanceMinute = await getCouponTotalBalanceMinute(new Date(result.usageDate))
        setCouponTotalBalanceMinute(couponTotalBalanceMinute)
      }
      
      deletingReservationData.projectMst.medicalDocUsage =
      deletingReservationData.projectMst.medicalDocUsage === MEDICAL_DOC_USAGE.BY_FACILITY 
        ? deletingReservationData.medicalDoc
        : deletingReservationData.projectMst.medicalDocUsage;
      
      //decode_mstのデータを参照して当日入力（施設）の値をラベルに変換する
      const eatingMethodValueLables = decodeMstValue
      ? decodeMstValue.eatingMethod.map((item) => {
        return {
          value: item.code,
          label: item.name,
        }
      })
      : null
      const todayConditionInputFacilityMealValueLables = decodeMstValue
      ? decodeMstValue.todayConditionInputFacilityMeal.map((item) => {
        return {
          value: item.code,
          label: item.name,
        }
      })
      : null
      const defecationConditionValueLables = decodeMstValue
      ? decodeMstValue.defecationCondition.map((item) => {
        return {
          value: item.code,
          label: item.name,
        }
      })
      : null

      const eatingMethodLabel = (eatingMethodValueLables && deletingReservationData.todayConditionFacility.eatingMethod)
        ? eatingMethodValueLables.find(((valueLabel) => valueLabel.value === deletingReservationData.todayConditionFacility.eatingMethod))?.label
        : null
      const mealWhat1Label = (todayConditionInputFacilityMealValueLables && deletingReservationData.todayConditionFacility.mealWhat1)
        ? deletingReservationData.todayConditionFacility.mealWhat1.split(',').map((item) => todayConditionInputFacilityMealValueLables.find(((valueLabel) => valueLabel.value === item))?.label).join(',')
        : null
      const mealWhat2Label = (todayConditionInputFacilityMealValueLables && deletingReservationData.todayConditionFacility.mealWhat2)
        ? deletingReservationData.todayConditionFacility.mealWhat2.split(',').map((item) => todayConditionInputFacilityMealValueLables.find(((valueLabel) => valueLabel.value === item))?.label).join(',')
        : null
      const defecationCondition1Label = (defecationConditionValueLables && deletingReservationData.todayConditionFacility.defecationCondition1)
        ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === deletingReservationData.todayConditionFacility.defecationCondition1))?.label
        : null
      const defecationCondition2Label = (defecationConditionValueLables && deletingReservationData.todayConditionFacility.defecationCondition2)
        ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === deletingReservationData.todayConditionFacility.defecationCondition2))?.label
        : null
      const defecationCondition3Label = (defecationConditionValueLables && deletingReservationData.todayConditionFacility.defecationCondition3)
        ? defecationConditionValueLables.find(((valueLabel) => valueLabel.value === deletingReservationData.todayConditionFacility.defecationCondition3))?.label
        : null
        
      const todayConditionFacility: ReservationDetail['todayConditionFacility'] = {
        ...deletingReservationData.todayConditionFacility,
        eatingMethod: eatingMethodLabel ? eatingMethodLabel : null,
        mealWhat1: mealWhat1Label ? mealWhat1Label : null,
        mealWhat2: mealWhat2Label ? mealWhat2Label : null,
        defecationCondition1: defecationCondition1Label ? defecationCondition1Label : null,
        defecationCondition2: defecationCondition2Label ? defecationCondition2Label : null,
        defecationCondition3: defecationCondition3Label ? defecationCondition3Label : null,
      }

      //予約詳細をstateで保持する
      setReservationDetail({...deletingReservationData, todayConditionFacility: todayConditionFacility})
    },
    [reservationNo, isCanUseCouponSysCtrl, attachAccessData]
  )

  const openCouponUsageDialog = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000033 })

    setIsOpenCouponUsageDialog(true)
  }, [addOperationLog])

  const onCloseCouponUsageDialog = useCallback(
    async (isDecision?: boolean) => {
      setIsOpenCouponUsageDialog(false)
      if (isDecision) {
        // 確定で閉じたとき再読み込み
        await initialize()
      }
    },
    [initialize]
  )

  const decisionCouponUsage = useCallback(
    async (usageMinute: number) => {
      const rsvDetail = castNonNullable(reservationDetail)
      addOperationLog({
        operationId: OperationId.OP_00000034,
        accessData: [
          ...rsvDetail.childInfoList.map((childInfo) => ({
            userIdRegFlag: yesNo.yes,
            childId: childInfo.childId,
            usageDate: toApiYmd(rsvDetail.usageDate),
            reservationNo: rsvDetail.reservationNo,
          }))
        ],
      })

      const res = await postCouponUsage(reservationNo, usageMinute)
      if (res.resultCode) {
        return { errorMessage: translate(`facilityReservationDetail.error.couponUsageResultCode${res.resultCode}`) }
      }
    },
    [reservationNo, addOperationLog, reservationDetail]
  )

  useEffect(() => {
    addOperationLog({ operationId: OperationId.OP_00000001 })

    dispatch(showLoading(errorHandle(() => initialize(true))))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const goCancel = useCallback(
    (status: string) => {
      if (status === reservationStatus.wait) {
        addOperationLog({ operationId: OperationId.OP_00000032 })
      } else {
        addOperationLog({ operationId: OperationId.OP_00000031 })
      }

      history.push(facilityReservationDeleteUrl.url(reservationNo))
    },
    [history, reservationNo, addOperationLog]
  )

  const goChange = useCallback(() => {
    addOperationLog({ operationId: OperationId.OP_00000035 })

    history.push(facilityReservationSelectionUpdateUrl.url(reservationNo))
  }, [history, reservationNo, addOperationLog])

  const goTodayConditionInput = useCallback(() => {
    //確認画面から当画面に戻ってくる時のために入力値をreduxで保持している
    //reduxに入力値を保持し続けると他の予約に対する当日入力時にも入力値を引き継いでしまうためクリアする
    dispatch(clearReservationInfo())
    dispatch(clearTodayConditionInputFieldsInfo())
    dispatch(clearInput())

    history.push(facilityReservationTodayConditionInputUrl.url(reservationNo))
  }, [history, reservationNo, addOperationLog])

  return {
    reservationDetail,
    reservationUsageMinute,
    isCanUseCoupon,
    isUsedCoupon,
    goCancel,
    goChange,
    goTodayConditionInput,
    couponTotalBalanceMinute,
    isOpenCouponUsageDialog,
    openCouponUsageDialog,
    onCloseCouponUsageDialog,
    decisionCouponUsage,
  }
}

const getReservationDetailWithRequirementSetting = async (reservationNo: string, decodeMstValue: ReservationSettingDecodeMst) => {
  const [
    { 
      reservationDatetime, 
      reservationChangeDatetime, 
      couponUsageMinute,
      ...reservation
    }, useReason] = await Promise.all([
    getReservation(reservationNo, decodeMstValue),
    getUseReason(),
  ])
  
  const facility = await getFacility(reservation.facilityId, reservation.usageDate)
  const projectMst = await getProjectMstDataToRefer(reservation.facilityId, [reservation.usageDate], facility.projectId)
  const detailSetting = (facility.detailSetting.length === 1 && facility.detailSetting[0].useReasonCode === '0') ? facility.detailSetting[0] : facility.detailSetting.find((v) => v.useReasonCode === reservation.useReasonCategory)
  
  const currAnswers = {...reservation.childInfoList[0].facilityInputFields};
  reservation.childInfoList[0].facilityInputFields = {};
  facility.facilityInputFields?.forEach((field) => {
    const answer = currAnswers[field.id];
    reservation.childInfoList[0].facilityInputFields[field.id] = {
      answer: answer?.answer ?? "", 
      id: answer?.id ?? null, 
      updateDatetime: answer?.updateDatetime ?? null, 
      order: field.order,
    }
  })

  return {
      ...reservation,
      ...nullPropsToUndefined({
      couponUsageMinute,
      immediatelyReservationFlag: facility.immediatelyReservationFlag,
      lunchAcceptFlag: facility.lunchAcceptFlag,
      snackAcceptFlag: facility.snackAcceptFlag,
      postponeCancelWaitAcceptFlag: facility.postponeCancelWaitAcceptFlag,
      useReason,   
      cancelLimit: detailSetting?.cancelLimit,
      cancelLimitTime: fromApiHms(detailSetting?.cancelLimitTime)?.getTime(),
      cancelNote: detailSetting?.cancelNote,
      cancelExcludeHolidayFlag: detailSetting?.cancelExcludeHolidayFlag,
      cancelLimitHolidayOption: detailSetting?.cancelLimitHolidayOption,
      reservationDatetime: reservationDatetime,
      reservationChangeDatetime: reservationChangeDatetime,
      // クーポン利用入力上限時間
      couponUsageInputLimitMinute:facility.couponUsageInputLimitTime &&  facility.couponUsageInputLimitTime * 60,
      projectMst,
      medicalDoc: facility.medicalDoc,
      isUseReasonCategoryHidden: isUseReasonCategoryHidden(reservation.useReasonCategory, facility)
    })
  }
}

const getCouponTotalBalanceMinute = async (usageDate: Date) => {
  const couponsRes = await executeGetCouponsIssues({ targetDate: toApiYmd(usageDate) })
  const coupons = couponsRes.result
  if (coupons.length) {
    // 有効なクーポンが存在する場合
    return coupons.reduce((sumVal, coupon) => sumVal + coupon.totalBalanceMinute, 0)
  }
}

const postCouponUsage = (reservationNo: string, usageMinute: number) => {
  return executePostCouponsUsage(reservationNo, { usageMinute })
}
